import { Field } from '@base-ui/react/field';
import { Icon, WarningIcon } from '@phosphor-icons/react';
import React, { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';

import { CharacterCount } from './characterCount';

type InputFieldSharedProps = {
  state?: 'default' | 'error';
  icon?: Icon;
  onIconClick?: () => void;
  errorMessage?: string;
  inputClassName?: string;
  wrapperClassName?: string;
  fullWidth?: boolean;
  /** When set, renders a codepoint ``count/limit`` counter beneath the input. */
  characterLimit?: number;
};

type InputFieldType = 'text' | 'email' | 'password' | 'search' | 'date' | 'number' | 'datetime-local' | 'time';

export type InputFieldProps = InputHTMLAttributes<HTMLInputElement> &
  InputFieldSharedProps & {
    type?: InputFieldType;
  };

export type TextareaInputFieldProps = TextareaHTMLAttributes<HTMLTextAreaElement> &
  InputFieldSharedProps & {
    type: 'textarea';
  };

type InputFieldAllProps = InputFieldProps | TextareaInputFieldProps;

type InputFieldComponent = React.ForwardRefExoticComponent<InputFieldProps & React.RefAttributes<HTMLInputElement>> & {
  (props: TextareaInputFieldProps & React.RefAttributes<HTMLTextAreaElement>): React.ReactElement | null;
  (props: InputFieldProps & React.RefAttributes<HTMLInputElement>): React.ReactElement | null;
};

const baseStyle =
  'w-full h-[36px] px-[13px] py-[8px] gap-2 rounded-[6px] text-[14px] font-medium font-raleway leading-[100%]';

const borderColor = {
  default: 'border-border-default focus-within:border-border-active',
  error: 'border-misc-error focus-within:border-misc-error focus-within:ring-2 focus-within:ring-misc-error'
};

const textColor = {
  default: 'text-content-subtle focus-within:text-content-default',
  error: 'text-content-default'
};

const iconColor = {
  default: 'text-content-subtlest',
  error: 'text-content-subtlest'
};

const InputFieldInner = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputFieldAllProps>((props, ref) => {
  const {
    state = 'default',
    type = 'text',
    icon,
    onIconClick,
    errorMessage,
    className,
    fullWidth = false,
    wrapperClassName,
    inputClassName,
    disabled,
    characterLimit,
    onFocus,
    onBlur,
    ...inputProps
  } = props;
  const visualState = state === 'error' ? 'error' : 'default';
  const valueAsString = typeof inputProps.value === 'string' ? inputProps.value : '';
  const [focused, setFocused] = React.useState(false);
  const controlRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const setControlRef = React.useCallback(
    (node: HTMLInputElement | HTMLTextAreaElement | null) => {
      controlRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  return (
    <Field.Root
      invalid={visualState === 'error'}
      disabled={disabled}
      className={`${fullWidth ? 'w-full' : ''} flex flex-col gap-[4px] ${className ?? ''}`}
    >
      <div
        className={`flex items-center border-[2px] bg-white ${baseStyle} ${wrapperClassName || ''} ${borderColor[visualState]} ${textColor[visualState]} ${disabled ? 'opacity-60 cursor-not-allowed bg-bluegray-50' : 'cursor-text'}`}
        onMouseDown={(event) => {
          if (disabled || event.target !== event.currentTarget) {
            return;
          }
          event.preventDefault();
          controlRef.current?.focus();
        }}
      >
        <Field.Control
          ref={setControlRef}
          type={type === 'textarea' ? undefined : type}
          disabled={disabled}
          className={`w-full min-w-0 outline-none bg-transparent ${inputClassName || ''}`}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event as never);
          }}
          onBlur={(event) => {
            setFocused(false);
            onBlur?.(event as never);
          }}
          {...(inputProps as InputHTMLAttributes<HTMLInputElement>)}
          render={(controlProps) =>
            type === 'textarea' ? <textarea {...controlProps} /> : <input {...controlProps} />
          }
        />
        {icon && (
          <div className={`${iconColor[visualState]} ${onIconClick ? 'cursor-pointer' : ''}`} onClick={onIconClick}>
            {React.createElement(icon, { size: 20, weight: 'regular' })}
          </div>
        )}
      </div>
      {characterLimit !== undefined && focused && <CharacterCount value={valueAsString} limit={characterLimit} />}
      {visualState === 'error' && errorMessage && (
        <Field.Error
          match
          className="flex items-center gap-[10px] text-[#FF0000] font-raleway font-normal text-[14px] leading-[120%] tracking-[0] text-center align-middle mt-[3px]"
        >
          <span>
            <WarningIcon size={20} weight="regular" color="#FF0000" />
          </span>
          <span>{errorMessage}</span>
        </Field.Error>
      )}
    </Field.Root>
  );
});

export const InputField = InputFieldInner as InputFieldComponent;

export default InputField;
